import { normalizeMultiPrimaryDefaults } from '@/components/Form/_hooks/normalizeMultiPrimaryDefaults';
import { EMPTY_PRIMARY_ROW } from '@/components/Form/FormAutocompleteField/MultiPrimaryAutocompleteField';
import type { Hcp } from '@/core/graphql/generated/types';

import type { HcpDcrValuesIn } from '../_schemas/hcpSchema';

import { mapAddressesIn } from './mapAddressesIn';
import { mapDegreesIn } from './mapDegreesIn';
import { mapGlobalSpecialties } from './mapGlobalSpecialties';
import { mapLicensesIn } from './mapLicensesIn';
import { mapParentHcosIn } from './mapParentHcosIn';

/**
 * The map represents a mapping between Hcp fields and HcpDcrValuesIn fields for custom app fields used in DCR form.
 */
export const DCR_CUSTOM_HCP_FIELDS: Partial<
	Record<keyof Hcp, keyof HcpDcrValuesIn>
> = {
	allDegreeCda_V: 'degrees',
	degree1Cda_V: 'degrees'
};

// Note(Filip): Some of these fields are hidden or not editable in the DCR form as of 15.12.2025 as Jonas requested; however, they will be displayed in the future
export const HCP_EMPTY_DEFAULTS_IN: HcpDcrValuesIn = {
	// Name
	firstNameCda_V: '',
	lastNameCda_V: '',
	middleNameCda_V: '',
	prefixCda_V: '',
	suffixCda_V: '',

	// Demographics
	languageCda_V: '',
	gender_V: '',
	birthYear_V: undefined,
	dataPrivacyOptOut_V: undefined,
	// gradSchool_V: '',
	// gradYear_V: '',

	// Contact
	// Note(Filip): As of 27.01.2026 - Jonas reqested these to be hidden for the time being
	// officePhoneCda_V: '',
	phone2_V: '',
	// faxCda_V: '',
	emailCda_V: '',
	email2_V: '',
	email3_V: '',
	email4_V: '',
	email5_V: '',
	websiteCda_V: '',

	// Professional
	hcpType: '',
	hcpTypeLocal_VId: '',
	statusCda_V: '',
	nhidCda_V: '',
	nhid2_V: '',
	degrees: [],
	hcpSpecialties: [],
	allSpecLocal_VId: [EMPTY_PRIMARY_ROW],
	// localSpecialties: [EMPTY_PRIMARY_ROW],
	// hcpTaxId_V: '',
	// prescriberCda_V: '',
	// inCurrentPecos_V: '',
	// telehealth_V: '',

	// Addresses
	addresses: [],

	// Licenses
	licenses: [],

	// Affiliations
	parenthcos: [],
	partnerhcos: [],

	newEntityCountryId: ''
};

const unwrapEmail = (field: Hcp['email']): string =>
	field?.__typename === 'EmailValue' ? field.value : '';

export const mapHcpToFormDefaultsIn = (hcp: Hcp): HcpDcrValuesIn => {
	return {
		// Name
		firstNameCda_V: hcp.firstNameCda_V ?? '',
		lastNameCda_V: hcp.lastNameCda_V ?? '',
		middleNameCda_V: hcp.middleNameCda_V ?? '',
		prefixCda_V: hcp.prefixCda_V ?? '',
		suffixCda_V: hcp.suffixCda_V ?? '',

		// Demographics
		languageCda_V: hcp.languageCda_V ?? '',
		gender_V: hcp.gender_V ?? '',
		birthYear_V:
			typeof hcp.birthYear_V === 'number' ? hcp.birthYear_V : undefined,
		dataPrivacyOptOut_V: hcp.dataPrivacyOptOut_V ?? false,

		// Contact
		// Note(Filip): As of 27.01.2026 - Jonas reqested this to be hidden for the time being
		// officePhoneCda_V: hcp.officePhoneCda_V ?? '',
		phone2_V: hcp.phone2_V ?? '',
		// Note(Filip): As of 27.01.2026 - Jonas reqested this to be hidden for the time being
		// faxCda_V: hcp.faxCda_V ?? '',
		emailCda_V: unwrapEmail(hcp?.email),
		email2_V: unwrapEmail(hcp?.email2),
		email3_V: unwrapEmail(hcp?.email3),
		email4_V: unwrapEmail(hcp?.email4),
		email5_V: unwrapEmail(hcp?.email5),
		websiteCda_V: hcp.websiteCda_V,

		// Local Attributes
		// Note(Filip): As of 27.01.2026 - Jonas reqested this to be hidden for the time being
		// Note(Filip): When enabling this, you would also need to enable the related validator in hcpSchema.ts, the place where the component is used, and `mapHcpToFormDefaultsIn.ts`
		/*
		gradSchool_V: hcp.gradSchool_V ?? '',
		gradYear_V: hcp.gradYear_V ?? '',
		prescriberCda_V: hcp.prescriberCda_V ?? false,
		inCurrentPecos_V: hcp.inCurrentPecos_V ?? false,
		telehealth_V: hcp.telehealth_V ?? false,
		*/

		// Professional
		hcpType: hcp.hcpType?.name ?? '',
		hcpTypeLocal_VId: hcp.localHcpType?.name ?? '',
		statusCda_V: hcp.statusCda_V ?? '',
		nhidCda_V: hcp.nhidCda_V ?? '',
		nhid2_V: hcp.nhid2_V ?? '',

		// Note(Filip): As of Jan 2026, in the DCR HCP form, we only display the primary med degree - `degree1Cda_V`; Also, be careful of duplicates - `allDegreeCda_V` could contain `degree1Cda_V`
		// Medical Degrees
		degrees: normalizeMultiPrimaryDefaults(
			mapDegreesIn(hcp.degree1Cda_V, hcp.allDegreeCda_V)
		),

		hcpSpecialties: normalizeMultiPrimaryDefaults(
			mapGlobalSpecialties(hcp.hcpSpecialties)
		),

		allSpecLocal_VId: normalizeMultiPrimaryDefaults(
			[
				hcp.primaryLocalSpecialty?.name,
				hcp.secondaryLocalSpecialty?.name
			].filter((n): n is string => !!n)
		),

		// Addresses
		addresses: mapAddressesIn(hcp.addresses),

		// Licenses
		licenses: mapLicensesIn(hcp.licenses),

		// Affiliations
		parenthcos: mapParentHcosIn(
			hcp.affiliationSearch?.__typename === 'HCPAffiliationsResults'
				? hcp.affiliationSearch.results
				: undefined
		),
		partnerhcos: []
	};
};
